import { useLayoutEffect, useRef, useState } from "react";

type UseAutoFitTextOptions = {
  /** Maximum font size in pixels (the "natural" size to start from). */
  maxFontSize?: number;
  /** Minimum font size in pixels — won't shrink below this. */
  minFontSize?: number;
  /**
   * Optional list of dependencies. When any of these change, the hook
   * re-runs the fit. Useful when the displayed text is a prop that may
   * change without the element being unmounted (e.g. sheet title).
   */
  deps?: ReadonlyArray<unknown>;
};

type UseAutoFitTextReturn<T extends HTMLElement> = {
  /** Ref to attach to the text element whose font-size should auto-fit. */
  ref: React.RefObject<T>;
  /** Computed font-size in pixels actually applied. */
  fontSize: number;
};

/**
 * Auto-fit a single-line text inside its parent container by shrinking the
 * font-size from `maxFontSize` down to `minFontSize` (1px steps) until the
 * text no longer overflows horizontally.
 *
 * The text element it is attached to is forced to render on a single line
 * (`whiteSpace: "nowrap"`). The hook itself sets `fontSize` and `whiteSpace`
 * inline on the element, so callers don't need to do that themselves.
 *
 * Re-fits on parent resize via `ResizeObserver`, and whenever any value in
 * `deps` changes (typically: the text content).
 */
export function useAutoFitText<T extends HTMLElement = HTMLElement>({
  maxFontSize = 24,
  minFontSize = 12,
  deps = [],
}: UseAutoFitTextOptions = {}): UseAutoFitTextReturn<T> {
  const ref = useRef<T>(null);
  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  // Use layout effect so the user never sees an overflowing flash before the
  // first measurement settles.
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    // Force single-line rendering — auto-fit is only meaningful in 1D.
    el.style.whiteSpace = "nowrap";

    const fit = () => {
      let size = maxFontSize;
      el.style.fontSize = `${size}px`;
      // scrollWidth includes overflowed content, clientWidth is the visible
      // box. Shrink until the text no longer overflows, or we hit the floor.
      while (el.scrollWidth > parent.clientWidth && size > minFontSize) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
      setFontSize(size);
    };

    fit();

    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxFontSize, minFontSize, ...deps]);

  return { ref, fontSize };
}
